import type { Object3D } from 'three';
import { onBeforeUnmount, ref } from 'vue';
import type { Subscription } from 'rxjs';
import {
  clearSketchEntityReferenceSourceHighlight,
  isFeatureFace,
  isFeatureLine,
  markSketchEntityReferenceSourceHighlight,
  resolveSketchEntityReferenceSelectionTargetObject,
  SketchEntityReferenceUIBridge,
  resolveExternalGeometrySourceDescriptorKey,
  type ExecutionDiagnostic,
  type EntitySelectionMode,
  type ExternalGeometrySourceDescriptor,
  type SelectionUpdatedEventPayload,
  type SketchEntityReferenceSelectionMutationResult,
} from '@puppycad/cad-core';
import type { ConvertEntitiesSelectedItem, ConvertEntitiesState } from '@/core';
import type {
  GeometryReferenceWorkflowDependencies,
  UseEditorGeometryReferenceWorkflowOptions,
  UseEditorGeometryReferenceWorkflowResult,
} from '../../../types/editor/GeometryReferenceWorkflow';

/** Convert Entities 中需要保留内部诊断但不打扰用户的诊断类型。 */
const SILENT_GEOMETRY_REFERENCE_DIAGNOSTIC_KINDS = new Set<string>([
  'sampled-curve-fit-warning',
]);

/**
 * 构建 Convert Entities 命令会话状态。
 *
 * @param targetSketchGroupId 当前目标草图组 ID。
 * @param selectedItems 当前已选实体列表。
 * @param active 当前会话是否激活。
 * @param canConfirm 当前是否允许确认。
 * @returns 归一化后的会话状态。
 */
function buildConvertEntitiesState(
  targetSketchGroupId: string | null,
  selectedItems: ConvertEntitiesSelectedItem[] = [],
  active = false,
  canConfirm = false,
  asConstruction = false,
  selectChain = false,
  innerLoopsOneByOne = false,
  canSelectAllInnerLoops = false,
  isResolvingFaceBoundaries = false,
): ConvertEntitiesState {
  return {
    active,
    targetSketchGroupId,
    selectedItems,
    asConstruction,
    selectChain,
    innerLoopsOneByOne,
    canSelectAllInnerLoops,
    isResolvingFaceBoundaries,
    canConfirm,
  };
}

/**
 * 构建 Convert Entities 选择项稳定键。
 *
 * @param descriptor 外部来源描述。
 * @returns 可用于 Map 存储的稳定键。
 */
function buildConvertEntitiesSelectionKey(
  descriptor: ExternalGeometrySourceDescriptor,
): string {
  return resolveExternalGeometrySourceDescriptorKey(descriptor);
}

interface RelatedPointCarrier extends Object3D {
  startPoint?: Object3D;
  endPoint?: Object3D;
  getRelatedSketchPointsSnapshot?: () => readonly Object3D[] | Object3D[];
}

/**
 * 判断对象是否支持草图外部高亮状态。
 *
 * @param object 目标对象。
 * @returns 支持时返回 `true`。
 */
function isSketchStateHighlightableObject(
  object: Object3D,
): boolean {
  return Reflect.get(object, 'isSketchLineObject') === true
    || isFeatureFace(object)
    || isFeatureLine(object)
    || Reflect.get(object, 'isSketchSplineObject') === true
    || Reflect.get(object, 'isSketchStyleSplineObject') === true
    || Reflect.get(object, 'isSketchCircleObject') === true
    || Reflect.get(object, 'isSketchArcObject') === true
    || Reflect.get(object, 'isSketchEllipseObject') === true
    || Reflect.get(object, 'isSketchParabolaObject') === true;
}

/**
 * 遍历 Convert Entities 视觉对象树。
 * 说明：
 * 1. preview 根对象的控制点有时会挂在根对象子树中，有时会直接挂到目标草图；
 * 2. 因此这里同时遍历 `children` 与约定俗成的关联点入口；
 * 3. 使用 visited 去重，避免同一控制点被重复处理。
 *
 * @param root 遍历起点。
 * @param visitor 访问回调。
 */
function walkConvertEntitiesVisualTree(
  root: Object3D,
  visitor: (object: Object3D) => void,
): void {
  const visited = new Set<Object3D>();

  const visit = (object: Object3D): void => {
    if (visited.has(object)) {
      return;
    }

    visited.add(object);
    visitor(object);
    object.children.forEach((child) => {
      visit(child);
    });

    const relatedPointCarrier = object as RelatedPointCarrier;
    if (relatedPointCarrier.startPoint) {
      visit(relatedPointCarrier.startPoint);
    }
    if (relatedPointCarrier.endPoint) {
      visit(relatedPointCarrier.endPoint);
    }
    relatedPointCarrier.getRelatedSketchPointsSnapshot?.().forEach((pointObject) => {
      visit(pointObject);
    });
  };

  visit(root);
}

/**
 * 将 UI bridge 选择快照转为 PropertyManager 展示项。
 *
 * @param uiBridge 当前 UI bridge。
 * @returns 展示项列表。
 */
function buildSelectedItems(
  uiBridge: SketchEntityReferenceUIBridge | null,
): ConvertEntitiesSelectedItem[] {
  if (!uiBridge) {
    return [];
  }

  return uiBridge.getSelections().map((selection) => ({
    id: selection.selectionId,
    label: selection.displayLabel,
    kind: selection.displayKind,
  }));
}

/**
 * 判断诊断中是否包含 error。
 *
 * @param diagnostics 诊断列表。
 * @returns 是否包含 error。
 */
function hasErrorDiagnostics(
  diagnostics: ExecutionDiagnostic[],
): boolean {
  return diagnostics.some((diagnostic) => diagnostic.level === 'error');
}

/**
 * 判断诊断是否需要展示给用户。
 *
 * @param diagnostic 当前诊断。
 * @returns 需要触发 toast 时返回 true。
 */
function isUserVisibleGeometryReferenceDiagnostic(
  diagnostic: ExecutionDiagnostic,
): boolean {
  return !SILENT_GEOMETRY_REFERENCE_DIAGNOSTIC_KINDS.has(diagnostic.kind);
}

/**
 * 读取优先级最高的诊断消息。
 *
 * @param diagnostics 诊断列表。
 * @returns 首条 error/warning 消息；没有时返回 `null`。
 */
function resolvePrimaryDiagnosticMessage(
  diagnostics: ExecutionDiagnostic[],
): string | null {
  const visibleDiagnostics = diagnostics.filter(isUserVisibleGeometryReferenceDiagnostic);
  const errorDiagnostic = visibleDiagnostics.find((diagnostic) => diagnostic.level === 'error');
  if (errorDiagnostic) {
    return errorDiagnostic.message;
  }

  return visibleDiagnostics[0]?.message ?? null;
}

/**
 * 编辑器几何引用工作流。
 *
 * 设计说明：
 * 1. 该层负责 Convert Entities 的宿主会话状态、选择订阅与 PropertyManager 同步；
 * 2. preview/create 的几何语义仍复用 cad-core 已建立的 UIBridge 与 Viewer 任务门面；
 * 3. 文档级重建和生命周期监听由 Viewer 内部 Controller 托管，当前层只持有命令级 Preview 会话。
 */
export function useEditorGeometryReferenceWorkflow(
  options: UseEditorGeometryReferenceWorkflowOptions,
): UseEditorGeometryReferenceWorkflowResult {
  const { dependencies } = options;
  const convertEntitiesState = ref<ConvertEntitiesState>(buildConvertEntitiesState(null));

  let uiBridge: SketchEntityReferenceUIBridge | null = null;
  let previewSession: ReturnType<GeometryReferenceWorkflowDependencies['createPreviewSession']> = null;
  let selectionSubscription: Subscription | null = null;
  let preselectionSubscription: Subscription | null = null;
  let preselectionSourceObjects: Object3D[] = [];
  let previewRefreshRevision = 0;
  let lastPreviewDiagnosticMessage: string | null = null;
  let isConfirming = false;
  let convertEntitiesAsConstruction = false;
  let convertEntitiesSelectChain = false;
  let convertEntitiesInnerLoopsOneByOne = false;
  let isResolvingFaceBoundaries = false;
  let faceBoundaryRequestRevision = 0;
  let selectionModeBeforeConvertEntities: EntitySelectionMode | null = null;
  const highlightedSourceObjects = new Map<string, Object3D>();
  const highlightedSourceKeysBySelectionId = new Map<string, string[]>();
  const highlightedSelectionIdsBySourceKey = new Map<string, Set<string>>();

  /**
   * 输出信息提示。
   *
   * @param message 提示内容。
   */
  const notifyInfo = (message: string): void => {
    options.notifyInfo?.(message);
  };

  /**
   * 输出警告提示。
   *
   * @param message 提示内容。
   */
  const notifyWarning = (message: string): void => {
    options.notifyWarning?.(message);
  };

  /**
   * 输出错误提示。
   *
   * @param message 提示内容。
   */
  const notifyError = (message: string): void => {
    options.notifyError?.(message);
  };

  /**
   * 输出成功提示。
   *
   * @param message 提示内容。
   */
  const notifySuccess = (message: string): void => {
    options.notifySuccess?.(message);
  };

  /**
   * 同步当前会话状态到响应式对象。
   *
   * @param canConfirm 当前是否允许确认。
   */
  const syncConvertEntitiesState = (canConfirm = false): void => {
    const selectedItems = buildSelectedItems(uiBridge);
    convertEntitiesState.value = buildConvertEntitiesState(
      uiBridge?.getTargetSketchId() ?? null,
      selectedItems,
      uiBridge !== null,
      canConfirm,
      convertEntitiesAsConstruction,
      convertEntitiesSelectChain,
      convertEntitiesInnerLoopsOneByOne,
      Boolean(uiBridge?.canSelectAllInnerLoops()) && !isResolvingFaceBoundaries,
      isResolvingFaceBoundaries,
    );
  };

  /**
   * 取消当前选择订阅。
   */
  const unsubscribeSelection = (): void => {
    selectionSubscription?.unsubscribe();
    selectionSubscription = null;
  };

  /**
   * 进入 Convert Entities 专用选择模式。
   * 说明：
   * 1. SOLIDWORKS 的 Convert Entities 输入框既消费边，也允许点面后展开其相邻边；
   * 2. 因此命令激活期间应临时切到 face-edge 模式，让模型边与模型面都能进入候选；
   * 3. 原模式由当前 workflow 托管，退出命令时必须恢复，避免影响普通草图编辑。
   */
  const activateConvertEntitiesSelectionMode = (): void => {
    if (!dependencies.getSelectionMode || !dependencies.setSelectionMode) {
      return;
    }

    selectionModeBeforeConvertEntities = dependencies.getSelectionMode();
    dependencies.setSelectionMode('face-edge', {
      preserveSelection: false,
      allowExternalSketchEntities: true,
    });
  };

  /**
   * 恢复进入 Convert Entities 前的选择模式。
   */
  const restoreConvertEntitiesSelectionMode = (): void => {
    if (!dependencies.setSelectionMode || !selectionModeBeforeConvertEntities) {
      selectionModeBeforeConvertEntities = null;
      return;
    }

    const previousSelectionMode = selectionModeBeforeConvertEntities;
    selectionModeBeforeConvertEntities = null;
    dependencies.setSelectionMode(previousSelectionMode, {
      preserveSelection: false,
      allowExternalSketchEntities: false,
    });
  };

  /**
   * 延后清空真实视口选择。
   * 说明：
   * 1. 若在 `SelectionUpdated` 回调栈内同步调用 `clearSelection()`，
   *    旧的选择结算仍可能继续补发一次 `EntitySelected(source)`；
   * 2. 这样会把真实的蓝色 Selected 态重新写回，表现成点击空白后颜色消不掉；
   * 3. 因此这里必须把清空动作延后到当前选择结算结束之后，再让选择中心发布空快照。
   */
  const scheduleSelectionClear = (): void => {
    const activeBridge = uiBridge;
    queueMicrotask(() => {
      if (!activeBridge || uiBridge !== activeBridge) {
        return;
      }
      dependencies.clearSelection();
    });
  };

  /**
   * 确保转换实体引用运行时监听已经建立。
   *
   * 说明：
   * 1. 新建引用会在 confirm 流程里自动调用；
   * 2. 历史恢复或项目恢复会直接回灌 Repository，因此仍需显式激活 Viewer 文档运行时；
   * 3. web-client 只提交任务，不再创建、持有或释放文档级 Coordinator。
   */
  const ensureConvertEntitiesReferenceRuntime = (): void => {
    dependencies.ensureReferenceRuntime();
  };

  /**
   * 请求交互状态系统重新计算视觉状态。
   */
  const requestVisualRefresh = (): void => {
    dependencies.getViewer()?.interactionStateManager?.requestVisualRefresh?.();
  };

  /**
   * 对 Convert Entities 选择视觉对象施加程序化高亮标记。
   * 说明：
   * 1. 来源高亮与 preview 一样走 cad-core 内部语义标记，不保留真实 Selected 状态；
   * 2. Face 高亮整面，链、环和普通边仍高亮各自的来源对象；
   * 3. 视觉对象与执行来源分离，避免 Face 的 outer Wire 边集合冒充面选择反馈。
   *
   * @param targetObject 需要高亮的来源对象。
   */
  const applySourceHighlightMarker = (targetObject: Object3D): void => {
    if (!isSketchStateHighlightableObject(targetObject)) {
      return;
    }

    walkConvertEntitiesVisualTree(targetObject, (object) => {
      markSketchEntityReferenceSourceHighlight(object);
    });
    requestVisualRefresh();
  };

  /**
   * 恢复 Convert Entities 选择视觉对象的程序化高亮标记。
   *
   * @param targetObject 需要恢复的来源对象。
   */
  const clearSourceHighlightMarker = (targetObject: Object3D): void => {
    if (!isSketchStateHighlightableObject(targetObject)) {
      return;
    }

    walkConvertEntitiesVisualTree(targetObject, (object) => {
      clearSketchEntityReferenceSourceHighlight(object);
    });
    requestVisualRefresh();
  };

  /**
   * 记录单个来源对象的高亮状态。
   * 说明：
   * 相邻面可能共享同一条 topology 边，因此同一个 source key 需要记录多个 selection 引用，
   * 只有最后一个引用被移除时才真正清除材质高亮。
   *
   * @param selectionKey 来源稳定键。
   * @param targetObject 命中的来源对象。
   * @param selectionId 引用该来源的选择项 ID。
   */
  const setHighlightedSourceObject = (
    selectionKey: string,
    targetObject: Object3D,
    selectionId: string,
  ): void => {
    const previousTargetObject = highlightedSourceObjects.get(selectionKey);
    if (previousTargetObject && previousTargetObject !== targetObject) {
      clearSourceHighlightMarker(previousTargetObject);
    }

    highlightedSourceObjects.set(selectionKey, targetObject);
    const selectionIds = highlightedSelectionIdsBySourceKey.get(selectionKey) ?? new Set<string>();
    selectionIds.add(selectionId);
    highlightedSelectionIdsBySourceKey.set(selectionKey, selectionIds);
    applySourceHighlightMarker(targetObject);
  };

  /**
   * 移除单个来源对象高亮。
   *
   * @param selectionKey 来源稳定键。
   * @param selectionId 需要释放的选择项 ID；未提供时清理该来源的全部引用。
   */
  const clearHighlightedSourceObject = (
    selectionKey: string,
    selectionId?: string,
  ): void => {
    const targetObject = highlightedSourceObjects.get(selectionKey);
    if (!targetObject) {
      return;
    }

    const selectionIds = highlightedSelectionIdsBySourceKey.get(selectionKey);
    if (selectionId && selectionIds) {
      selectionIds.delete(selectionId);
      if (selectionIds.size > 0) {
        return;
      }
    }
    highlightedSelectionIdsBySourceKey.delete(selectionKey);
    highlightedSourceObjects.delete(selectionKey);
    const isStillReferenced = Array.from(highlightedSourceObjects.values())
      .some((remainingTargetObject) => remainingTargetObject === targetObject);
    if (!isStillReferenced) {
      clearSourceHighlightMarker(targetObject);
    }
  };

  /**
   * 记录一条用户选择意图展开后的全部高亮 source key。
   *
   * 说明：
   * 面、链、槽这类复合选择在 PropertyManager 中只有一条选择项，
   * 但运行时高亮需要落到多条 source 对象上，因此删除列表项时必须按选择意图反查全部 source key。
   *
   * @param selectionId PropertyManager 选择项稳定 ID。
   * @param selectionKeys 展开后的 source 稳定 key 列表。
   */
  const setSelectionHighlightKeys = (
    selectionId: string,
    selectionKeys: string[],
  ): void => {
    highlightedSourceKeysBySelectionId.set(
      selectionId,
      Array.from(new Set(selectionKeys)),
    );
  };

  /**
   * 清理一条用户选择意图展开出的全部高亮对象。
   *
   * @param selectionId PropertyManager 选择项稳定 ID。
   */
  const clearSelectionHighlightKeys = (selectionId: string): void => {
    const selectionKeys = highlightedSourceKeysBySelectionId.get(selectionId) ?? [selectionId];
    selectionKeys.forEach((selectionKey) => {
      clearHighlightedSourceObject(selectionKey, selectionId);
    });
    highlightedSourceKeysBySelectionId.delete(selectionId);
  };

  /**
   * 清空当前会话内全部来源高亮。
   */
  const clearSourceHighlights = (): void => {
    highlightedSourceObjects.forEach((targetObject) => {
      clearSourceHighlightMarker(targetObject);
    });
    highlightedSourceObjects.clear();
    highlightedSourceKeysBySelectionId.clear();
    highlightedSelectionIdsBySourceKey.clear();
  };

  /**
   * 把 cad-core 选择 mutation 投影为命令持久高亮。
   *
   * 说明：
   * 1. 兼容矩阵已经由 UIBridge 内部集合完成，当前层只消费差量；
   * 2. 先释放旧意图，再登记新意图，最终来源覆盖仍由 intentId 引用集合决定；
   * 3. fallback 只服务缺少运行时 sourceObject 的当前点击候选，不进入持久化。
   *
   * @param mutation cad-core 原子选择差量。
   * @param fallbackTargetObject 当前点击对象兜底。
   */
  const applySelectionMutationHighlights = (
    mutation: SketchEntityReferenceSelectionMutationResult,
    fallbackTargetObject?: Object3D,
  ): void => {
    mutation.removedSelections.forEach((selection) => {
      clearSelectionHighlightKeys(selection.intent.intentId);
    });

    mutation.addedSelections.forEach((selection) => {
      const highlightEntries = selection.visualSourceObject
        ? [{
            selectionKey: selection.intent.intentId,
            sourceObject: selection.visualSourceObject,
          }]
        : selection.sources
            .map((source) => ({
              selectionKey: buildConvertEntitiesSelectionKey(source.descriptor),
              sourceObject: source.sourceObject ?? fallbackTargetObject,
            }))
            .filter((entry): entry is {
              selectionKey: string;
              sourceObject: Object3D;
            } => entry.sourceObject !== undefined);
      highlightEntries.forEach((entry) => {
        setHighlightedSourceObject(
          entry.selectionKey,
          entry.sourceObject,
          selection.intent.intentId,
        );
      });
      setSelectionHighlightKeys(
        selection.intent.intentId,
        highlightEntries.map((entry) => entry.selectionKey),
      );
    });
  };

  /**
   * 同步 preview 对象的材质快照。
   * 说明：
   * 1. preview 对象由工作流动态创建，必须在挂载后把当前材质登记给交互态管理器；
   * 2. 这样后续做 overlay 分层或状态恢复时，不会把 preview 回退到错误基础态；
   * 3. 该逻辑沿用临时分支的 preview runtime 协调思路，但在当前架构中收口到 workflow。
   */
  const syncPreviewMaterialSnapshots = (): void => {
    const interactionStateManager = dependencies.getViewer()?.interactionStateManager;
    if (!interactionStateManager || !previewSession) {
      return;
    }

    previewSession.getPreviewEntityRoots().forEach((previewRoot) => {
      walkConvertEntitiesVisualTree(previewRoot, (targetObject) => {
        interactionStateManager.syncExternalMaterialSnapshot(targetObject);
      });
    });
  };

  /**
   * 应用 Convert Entities 命令态视觉分层。
   * 说明：
   * 1. 当前草图、其他草图、preview 与外部来源统一走 cad-core 的视觉分层器；
   * 2. preview 根对象使用根级快照，关联点由分层器自行补遍历；
   * 3. 会话退出时统一清空覆盖，避免 render order 残留。
   */
  const applyConvertEntitiesVisualLayering = (): void => {
    const viewer = dependencies.getViewer();
    if (!viewer || !uiBridge) {
      viewer?.clearSketchEditingVisualLayering?.();
      return;
    }

    const currentSketchGroup = viewer.sketchGroupManager?.getGroupById(uiBridge.getTargetSketchId()) ?? null;
    if (!currentSketchGroup) {
      viewer.clearSketchEditingVisualLayering?.();
      return;
    }

    viewer.syncSketchEditingVisualLayering?.({
      previewRoots: previewSession?.getPreviewEntityRoots() ?? [],
      highlightedExternalRoots: Array.from(new Set(highlightedSourceObjects.values())),
    });
  };

  /**
   * 清理预览对象。
   */
  const clearPreview = (): void => {
    previewRefreshRevision += 1;
    previewSession?.clearPreview();
    lastPreviewDiagnosticMessage = null;
    requestVisualRefresh();
  };

  /**
   * 统一退出当前会话。
   *
   * @param input 清理选项。
   */
  const teardownSession = (
    input: { clearSelection?: boolean } = {},
  ): void => {
    const { clearSelection = true } = input;
    unsubscribeSelection();
    restoreConvertEntitiesSelectionMode();

    // 命令终点必须销毁独占的 Preview 会话，而不是只暂时清空当前批次。
    // 先清空本地引用，避免 dispose 或视觉刷新期间的重入再次访问旧命令对象。
    const retiringPreviewSession = previewSession;
    previewSession = null;
    previewRefreshRevision += 1;
    retiringPreviewSession?.dispose();
    lastPreviewDiagnosticMessage = null;
    requestVisualRefresh();
    clearSourceHighlights();
    uiBridge = null;
    isConfirming = false;
    convertEntitiesAsConstruction = false;
    convertEntitiesSelectChain = false;
    convertEntitiesInnerLoopsOneByOne = false;
    isResolvingFaceBoundaries = false;
    faceBoundaryRequestRevision += 1;

    if (clearSelection) {
      dependencies.clearSelection();
    }

    convertEntitiesState.value = buildConvertEntitiesState(null);
    applyConvertEntitiesVisualLayering();
  };

  /**
   * 设置 Convert Entities 是否作为构造线创建。
   *
   * 说明：
   * 1. 该选项是 SolidWorks Convert Entities 的输出属性，不改变当前已选来源；
   * 2. 状态由 workflow 托管，确认时统一写入 cad-core create input；
   * 3. 切换时保留当前 canConfirm，避免 UI checkbox 影响已有 preview 诊断结果。
   *
   * @param value 是否作为构造线创建。
   */
  const setConvertEntitiesAsConstruction = (value: boolean): void => {
    convertEntitiesAsConstruction = value;
    syncConvertEntitiesState(convertEntitiesState.value.canConfirm);
  };

  /**
   * 设置 Convert Entities 是否按草图链选择扩展。
   *
   * 说明：
   * 该选项只影响后续视口选择解析，不改写已经进入列表的选择意图；
   * 已选项的来源与高亮继续由 UIBridge 中的选择快照托管。
   *
   * @param value 是否启用选择链。
   */
  const setConvertEntitiesSelectChain = (value: boolean): void => {
    convertEntitiesSelectChain = value;
    syncConvertEntitiesState(convertEntitiesState.value.canConfirm);
  };

  /**
   * 设置是否把后续 inner Wire 成员边点击提升为完整环。
   *
   * 该模式不改写既有选择，也不触发 Preview；只有后续视口点击消费它。
   */
  const setConvertEntitiesInnerLoopsOneByOne = (value: boolean): void => {
    convertEntitiesInnerLoopsOneByOne = value;
    syncConvertEntitiesState(convertEntitiesState.value.canConfirm);
  };

  /**
   * 原子选择当前全部 topology face 的 inner Wires。
   */
  const selectAllConvertEntitiesInnerLoops = async (): Promise<void> => {
    if (!uiBridge || isResolvingFaceBoundaries) {
      return;
    }
    const faceSources = uiBridge.getTopologyFaceSources();
    if (faceSources.length === 0) {
      return;
    }
    const activeBridge = uiBridge;
    const requestRevision = ++faceBoundaryRequestRevision;
    isResolvingFaceBoundaries = true;
    syncConvertEntitiesState(false);
    try {
      const candidates = await (
        dependencies.resolveAllInnerLoopCandidates?.(
          faceSources,
        ) ?? []
      );
      if (
        requestRevision !== faceBoundaryRequestRevision
        || uiBridge !== activeBridge
      ) {
        return;
      }
      const mutation = activeBridge.selectAllInnerLoops(candidates);
      if (!mutation.changed) {
        return;
      }
      applySelectionMutationHighlights(mutation);
      applyConvertEntitiesVisualLayering();
      await refreshPreview();
    } catch (error) {
      if (
        requestRevision === faceBoundaryRequestRevision
        && uiBridge === activeBridge
      ) {
        notifyWarning(error instanceof Error ? error.message : '选择所有内环失败。');
      }
    } finally {
      if (
        requestRevision === faceBoundaryRequestRevision
        && uiBridge === activeBridge
      ) {
        isResolvingFaceBoundaries = false;
        syncConvertEntitiesState(convertEntitiesState.value.canConfirm);
      }
    }
  };

  /**
   * 刷新当前 preview。
   */
  const refreshPreview = async (): Promise<void> => {
    if (!uiBridge || !previewSession) {
      return;
    }

    const selectedItems = buildSelectedItems(uiBridge);
    if (selectedItems.length === 0) {
      clearPreview();
      syncConvertEntitiesState(false);
      applyConvertEntitiesVisualLayering();
      return;
    }

    const revision = ++previewRefreshRevision;
    const previewResult = await previewSession.refreshPreview(uiBridge.buildCreateInput());
    if (revision !== previewRefreshRevision || !uiBridge) {
      return;
    }

    syncPreviewMaterialSnapshots();
    applyConvertEntitiesVisualLayering();
    requestVisualRefresh();

    const previewHasError = hasErrorDiagnostics(previewResult.diagnostics);
    syncConvertEntitiesState(!previewHasError && !isConfirming);

    const diagnosticMessage = resolvePrimaryDiagnosticMessage(previewResult.diagnostics);
    if (!diagnosticMessage) {
      lastPreviewDiagnosticMessage = null;
      return;
    }

    if (diagnosticMessage === lastPreviewDiagnosticMessage) {
      return;
    }

    lastPreviewDiagnosticMessage = diagnosticMessage;
    if (previewHasError) {
      notifyError(diagnosticMessage);
      return;
    }
    notifyWarning(diagnosticMessage);
  };

  /**
   * 处理当前命中的选择对象。
   */
  const handleSelectionUpdated = async (
    payload: SelectionUpdatedEventPayload,
  ): Promise<void> => {
    if (!uiBridge) {
      return;
    }

    const primarySelectedObject = resolveSketchEntityReferenceSelectionTargetObject(payload);
    const candidateTargets = [
      primarySelectedObject,
      payload.object,
    ].filter((targetObject): targetObject is Object3D => targetObject !== null);
    const uniqueCandidateTargets = Array.from(new Set(candidateTargets));
    if (uniqueCandidateTargets.length === 0) {
      // clearSelection() 发布的空快照只是一次性输入消费完成信号，
      // 不得反向刷新命令高亮、Preview 或 PropertyManager 状态。
      return;
    }

    const activeBridge = uiBridge;
    const requestRevision = ++faceBoundaryRequestRevision;
    let resolvedCandidate: {
      candidate: NonNullable<Awaited<ReturnType<
        GeometryReferenceWorkflowDependencies['resolveSelectionCandidate']
      >>>;
      targetObject: Object3D;
    } | null = null;
    for (const targetObject of uniqueCandidateTargets) {
      const resolverOptions = convertEntitiesInnerLoopsOneByOne
        ? {
            selectChain: convertEntitiesSelectChain,
            innerLoopsOneByOne: true,
          }
        : convertEntitiesSelectChain
          ? { selectChain: true }
          : undefined;
      const candidateTask = resolverOptions
        ? dependencies.resolveSelectionCandidate(
            targetObject,
            activeBridge.getTargetSketchId(),
            resolverOptions,
          )
        : dependencies.resolveSelectionCandidate(
            targetObject,
            activeBridge.getTargetSketchId(),
          );
      const candidate = candidateTask instanceof Promise
        ? await candidateTask
        : candidateTask;
      if (
        requestRevision !== faceBoundaryRequestRevision
        || uiBridge !== activeBridge
      ) {
        return;
      }
      if (candidate) {
        resolvedCandidate = {
          candidate,
          targetObject,
        };
        break;
      }
    }
    if (!resolvedCandidate?.candidate) {
      scheduleSelectionClear();
      return;
    }

    const mutation = uiBridge.applySelectionOperation({
      operation: 'toggle',
      candidate: resolvedCandidate.candidate,
    });
    if (!mutation.changed) {
      scheduleSelectionClear();
      return;
    }
    applySelectionMutationHighlights(mutation, resolvedCandidate.targetObject);
    applyConvertEntitiesVisualLayering();

    // Convert Entities 中的视口选择只作为一次性输入信号；
    // 真正持续的视觉状态由手动高亮映射与 overlay 分层共同维护，
    // 避免源对象长期残留在“真实选中态”。
    scheduleSelectionClear();
    syncConvertEntitiesState(false);
    void refreshPreview();
  };

  /**
   * 建立选择订阅。
   */
  const subscribeSelection = (): void => {
    unsubscribeSelection();
    const selectionUpdated$ = dependencies.observeSelectionUpdated();
    if (!selectionUpdated$) {
      return;
    }

    selectionSubscription = selectionUpdated$.subscribe((payload) => {
      void handleSelectionUpdated(payload);
    });
  };

  /**
   * 持续记录命令外最后一次稳定选择的原始命中对象。
   *
   * 说明：
   * 1. 通用选择器可能把槽口成员边提升为槽口等语义对象；
   * 2. Convert Entities 命令前预选必须优先消费 `selectionSourceObjects`，否则会把
   *    单边预选错误扩展为整个复合对象；
   * 3. 这里只缓存只读输入快照，不改变通用选择器状态，并在组件卸载时释放订阅。
   */
  const subscribePreselectionSourceObjects = (): void => {
    preselectionSubscription?.unsubscribe();
    const selectionUpdated$ = dependencies.observeSelectionUpdated();
    if (!selectionUpdated$) {
      preselectionSubscription = null;
      return;
    }

    preselectionSubscription = selectionUpdated$.subscribe((payload) => {
      if (uiBridge) {
        return;
      }
      const rawSourceObjects = payload.selectionSourceObjects ?? [];
      preselectionSourceObjects = [
        ...(rawSourceObjects.length > 0
          ? rawSourceObjects
          : payload.selectedObjects),
      ];
    });
  };

  /**
   * 进入 Convert Entities 命令会话。
   */
  const enterConvertEntities = (): void => {
    /**
     * 同一个 Convert Entities 命令只允许一个活动会话。
     * 重复点击保持幂等，不销毁重建 Preview，也不会产生 resume→suspend 闪烁。
     */
    if (uiBridge || previewSession) {
      return;
    }

    const targetSketchGroupId = dependencies.getCurrentSketchGroupId();
    if (!targetSketchGroupId) {
      notifyWarning('请先编辑草图，再使用转换实体引用。');
      return;
    }

    const nextPreviewSession = dependencies.createPreviewSession();
    if (!nextPreviewSession) {
      notifyWarning('当前视口尚未就绪，无法进入转换实体引用。');
      return;
    }

    // 必须在模式切换和真实选择清空前复制命令前预选。
    const preselectedObjects = preselectionSourceObjects.length > 0
      ? [...preselectionSourceObjects]
      : [...(dependencies.getSelectedObjects?.() ?? [])];
    preselectionSourceObjects = [];
    teardownSession({ clearSelection: false });

    uiBridge = new SketchEntityReferenceUIBridge({
      targetSketchId: targetSketchGroupId,
    });
    previewSession = nextPreviewSession;
    activateConvertEntitiesSelectionMode();
    dependencies.clearSelection();
    syncConvertEntitiesState(false);
    subscribeSelection();
    applyConvertEntitiesVisualLayering();
    if (preselectedObjects.length > 0) {
      const activeBridge = uiBridge;
      const requestRevision = ++faceBoundaryRequestRevision;
      void (async () => {
        for (const targetObject of preselectedObjects) {
          const candidateTask = dependencies.resolveSelectionCandidate(
            targetObject,
            targetSketchGroupId,
          );
          const candidate = candidateTask instanceof Promise
            ? await candidateTask
            : candidateTask;
          if (
            requestRevision !== faceBoundaryRequestRevision
            || uiBridge !== activeBridge
          ) {
            return;
          }
          if (!candidate) {
            continue;
          }
          const mutation = activeBridge.applySelectionOperation({
            operation: 'add',
            candidate,
          });
          if (mutation.changed) {
            applySelectionMutationHighlights(mutation, targetObject);
          }
        }
        syncConvertEntitiesState(false);
        applyConvertEntitiesVisualLayering();
        if (activeBridge.getSelections().length > 0) {
          await refreshPreview();
        }
      })();
    }
  };

  /**
   * 取消 Convert Entities 命令会话。
   */
  const cancelConvertEntities = (): void => {
    teardownSession();
  };

  /**
   * 从属性面板移除单个已选来源。
   * 说明：
   * 1. PropertyManager 不直接改写会话数组，统一委托给 workflow/bridge；
   * 2. 这样来源高亮、preview 与可确认状态都能复用同一条收口链；
   * 3. 若传入项不存在，则保持当前状态不变。
   *
   * @param selectionId 需要移除的选择项稳定键。
   */
  const removeConvertEntitiesSelection = (selectionId: string): void => {
    if (!uiBridge) {
      return;
    }

    const mutation = uiBridge.applySelectionOperation({
      operation: 'remove',
      selectionId,
    });
    if (!mutation.changed) {
      return;
    }

    applySelectionMutationHighlights(mutation);
    /**
     * 属性面板删除已选项时，同样要把视口中的真实 Selected 态一并清掉。
     * 否则最后一次点击过的来源对象仍可能残留蓝色选中材质，表现成“取消了列表项但高亮还在”。
     */
    dependencies.clearSelection();
    syncConvertEntitiesState(false);
    applyConvertEntitiesVisualLayering();
    void refreshPreview();
  };

  /**
   * 确认 Convert Entities 命令会话。
   */
  const confirmConvertEntities = async (): Promise<boolean> => {
    if (!uiBridge || isConfirming) {
      return false;
    }

    const selectedItems = buildSelectedItems(uiBridge);
    if (selectedItems.length === 0) {
      notifyInfo('请先选择要转换的实体。');
      return false;
    }

    const viewer = dependencies.getViewer();
    if (!viewer) {
      notifyWarning('当前视口尚未就绪，无法创建转换实体引用。');
      return false;
    }
    isConfirming = true;
    syncConvertEntitiesState(false);

    const createInput = uiBridge.buildCreateInput();
    createInput.options = {
      ...createInput.options,
      asConstruction: convertEntitiesAsConstruction,
    };
    const commitTask = dependencies.commitReferences(createInput);
    if (!commitTask) {
      isConfirming = false;
      notifyWarning('当前视口尚未就绪，无法创建转换实体引用。');
      return false;
    }
    let commitResult: Awaited<typeof commitTask>;
    try {
      commitResult = await commitTask;
    } catch (error) {
      /**
       * 提交拒绝不代表 Convert Entities 命令结束。
       * 当前会话继续持有 Preview suspend 责任，只复位确认态并刷新命令预览，
       * 让用户可以调整选择后再次确认；禁止 teardown 提前恢复消费 Feature Preview。
      */
      isConfirming = false;
      notifyError('转换实体引用创建失败');
      if (typeof window !== 'undefined' && typeof window.reportError === 'function') {
        window.reportError(error);
      }
      await refreshPreview();
      return false;
    }
    const commitHasError = hasErrorDiagnostics(commitResult.diagnostics);
    const primaryDiagnosticMessage = resolvePrimaryDiagnosticMessage(commitResult.diagnostics);

    isConfirming = false;

    if (commitHasError) {
      if (primaryDiagnosticMessage) {
        notifyError(primaryDiagnosticMessage);
      }
      await refreshPreview();
      return false;
    }

    if (primaryDiagnosticMessage) {
      notifyWarning(primaryDiagnosticMessage);
    }

    if (commitResult.createdReferenceIds.length > 0) {
      dependencies.ensureReferenceRuntime();
      notifySuccess('已创建转换实体引用。');
    } else {
      notifyInfo('当前未创建新的转换实体引用。');
    }
    teardownSession();
    return true;
  };

  /**
   * 删除草图关系。
   * 说明：
   * 1. 普通关系只删除 solver constraint；
   * 2. Convert Entities 产生的引用关系必须同步删除引用记录，否则 rebuild 会重新挂回关系；
   * 3. 具体判断和删除动作下沉到 cad-core，web-client 只负责把属性面板动作转交给统一入口。
   *
   * @param targetEntity 目标草图实体。
   * @param constraintId 约束 ID。
   * @returns 成功处理时返回 true。
   */
  const removeSketchRelation = async (
    targetEntity: unknown,
    constraintId: number,
  ): Promise<boolean> => {
    if (!targetEntity || typeof targetEntity !== 'object' || !Number.isFinite(constraintId)) {
      return false;
    }
    try {
      const removeTask = dependencies.removeRelation({
        targetEntity: targetEntity as Object3D,
        constraintId,
      });
      if (!removeTask) {
        return false;
      }
      const result = await removeTask;
      const errorDiagnostic = result.diagnostics.find((diagnostic) => diagnostic.level === 'error');
      if (errorDiagnostic) {
        notifyError(errorDiagnostic.message);
        return false;
      }
      const updatedReferences = result.updatedReferences ?? [];
      const removed = result.removedConstraintIds.length > 0
        || result.removedReferenceIds.length > 0
        || updatedReferences.length > 0;
      if (!removed) {
        requestVisualRefresh();
        return false;
      }
      requestVisualRefresh();
      return true;
    } catch (error) {
      notifyError('删除草图几何关系失败');
      if (typeof window !== 'undefined' && typeof window.reportError === 'function') {
        window.reportError(error);
      }
      return false;
    }
  };

  subscribePreselectionSourceObjects();

  onBeforeUnmount(() => {
    preselectionSubscription?.unsubscribe();
    preselectionSubscription = null;
    preselectionSourceObjects = [];
    teardownSession();
  });

  return {
    convertEntitiesState,
    enterConvertEntities,
    cancelConvertEntities,
    confirmConvertEntities,
    removeConvertEntitiesSelection,
    setConvertEntitiesAsConstruction,
    setConvertEntitiesSelectChain,
    setConvertEntitiesInnerLoopsOneByOne,
    selectAllConvertEntitiesInnerLoops,
    removeSketchRelation,
    ensureConvertEntitiesReferenceRuntime,
  };
}
